import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { getVisitorProfile, rememberSaved } from "@/lib/visitorProfile";

const SavedContext = createContext({
  saved: new Set(),
  toggleSave: (_id) => {},
  isSaved: (_id) => /** @type {boolean} */ (false),
  accountSynced: false,
});

/**
 * Visitor bookmarks are entirely browser-based, without an account or login.
 * The visitor profile keeps them for a renewable 60-day period alongside
 * cached place details, itineraries, notes and booking reminders.
 */
export function SavedProvider({ children }) {
  const [saved, setSaved] = useState(() => new Set(getVisitorProfile().saved));

  useEffect(() => { rememberSaved([...saved]); }, [saved]);

  const toggleSave = useCallback((id) => {
    if (!id) return;
    setSaved((previous) => {
      const next = new Set(previous);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);
  const isSaved = useCallback((id) => saved.has(String(id)), [saved]);

  return (
    <SavedContext.Provider value={{ saved, toggleSave, isSaved, accountSynced: false }}>
      {children}
    </SavedContext.Provider>
  );
}
export const useSaved = () => useContext(SavedContext);
